import { inject, Injectable } from "@angular/core";
import {
  HttpInterceptor,
  HttpInterceptorFn,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpResponse,
  HttpErrorResponse,
} from "@angular/common/http";
import { Observable } from "rxjs";
import { tap } from "rxjs/operators";
import { ToastService } from "../services/toast.service";
import { MessageBuilderService } from "../services/message-builder.service";
import { EntityKey, getEntityConfig } from "../constants/entity-registry";
import {
  SUPPRESS_SUCCESS_TOAST,
  SUPPRESS_ERROR_TOAST,
  CUSTOM_SUCCESS_MESSAGE,
  CUSTOM_ERROR_MESSAGE,
  ENTITY_KEY,
  CUSTOM_SUCCESS_DESCRIPTION,
} from "../tokens/toast.context";
import { HttpMethod } from "../types/toast.types";

/**
 * TOAST INTERCEPTOR (Functional)
 *
 * Functional interceptor for Angular's standalone API.
 * Use this with provideHttpClient(withInterceptors([toastInterceptor]))
 */
export const toastInterceptor: HttpInterceptorFn = (req, next) => {
  const toastService = inject(ToastService);
  const messageBuilder = inject(MessageBuilderService);

  return next(req).pipe(
    tap({
      next: (event: HttpEvent<unknown>) => {
        if (event instanceof HttpResponse) {
          handleSuccess(req, toastService, messageBuilder);
        }
      },
      error: (error: unknown) => {
        if (error instanceof HttpErrorResponse) {
          handleError(req, error, toastService, messageBuilder);
        }
      },
    }),
  );
};

/**
 * Handle successful HTTP responses
 * @param req - HTTP request
 * @param toastService - Toast service instance
 * @param messageBuilder - Message builder service instance
 */
function handleSuccess(
  req: HttpRequest<unknown>,
  toastService: ToastService,
  messageBuilder: MessageBuilderService,
): void {
  // Check if toast is suppressed
  if (req.context.get(SUPPRESS_SUCCESS_TOAST)) {
    return;
  }

  // Only show success toast for mutating operations
  if (!isMutatingOperation(req.method)) {
    return;
  }

  // Skip if it's a filter/search operation
  if (isFilterOperation(req.url)) {
    return;
  }

  // Check for custom message and description
  const customMessage = req.context.get(CUSTOM_SUCCESS_MESSAGE);
  const customDescription = req.context.get(CUSTOM_SUCCESS_DESCRIPTION) ?? undefined;

  if (customMessage) {
    toastService.success(customMessage, customDescription);
    return;
  }

  // Auto-generate message from entity registry
  const entityKey = getEntityKey(req);

  if (entityKey) {
    const message = messageBuilder.buildSuccessMessage(
      req.method as HttpMethod,
      entityKey,
    );
    toastService.success(message, customDescription);
  } else {
    toastService.success("Operazione completata con successo", customDescription);
  }
}

/**
 * Handle HTTP error responses
 * @param req - HTTP request
 * @param error - HTTP error response
 * @param toastService - Toast service instance
 * @param messageBuilder - Message builder service instance
 */
function handleError(
  req: HttpRequest<unknown>,
  error: HttpErrorResponse,
  toastService: ToastService,
  messageBuilder: MessageBuilderService,
): void {
  // Check if toast is suppressed
  if (req.context.get(SUPPRESS_ERROR_TOAST)) {
    return;
  }

  // Check for custom error message from context
  const customMessage = req.context.get(CUSTOM_ERROR_MESSAGE);
  if (customMessage) {
    toastService.error(customMessage);
    return;
  }

  // Check for error message from backend response (priority over generic messages)
  const backendMessage = extractBackendErrorMessage(error);
  if (backendMessage) {
    // Try to translate the backend message using entity translations
    const entityKey = getEntityKey(req);
    const translated = translateBackendMessage(backendMessage, entityKey);
    toastService.error(translated);
    return;
  }

  // Fall back to auto-generated error message from entity registry
  const entityKey = getEntityKey(req);
  const message = messageBuilder.buildErrorMessage(
    error.status,
    entityKey || undefined,
  );
  toastService.error(message);
}

/**
 * Extract error message from backend response
 * @param error - HTTP error response
 * @returns Error message string or null if not found
 */
function extractBackendErrorMessage(error: HttpErrorResponse): string | null {
  // Check if error.error exists and has a message property
  if (error.error && typeof error.error === 'object' && 'message' in error.error) {
    const message = error.error.message;
    // Handle both string and array of strings (class-validator errors)
    if (typeof message === 'string') {
      return message;
    }
    if (Array.isArray(message) && message.length > 0) {
      return message[0];
    }
  }
  return null;
}

/**
 * Translate a backend error message using entity-specific translations.
 * Supports both exact matches and prefix matches (for messages with dynamic parts).
 *
 * @param message - Backend error message (English)
 * @param entityKey - Entity key for looking up translations
 * @returns Translated message if found, original message otherwise
 */
function translateBackendMessage(
  message: string,
  entityKey: EntityKey | null,
): string {
  if (!entityKey) return message;

  const entity = getEntityConfig(entityKey);
  const translations = entity.translations;
  if (!translations) return message;

  // Try exact match first
  if (translations[message]) {
    return translations[message];
  }

  // Try prefix match (for messages with dynamic parts like counts)
  const prefixKey = Object.keys(translations).find((key) =>
    message.startsWith(key),
  );
  if (prefixKey) {
    return translations[prefixKey];
  }

  return message;
}

/**
 * Check if HTTP method is a mutating operation
 * @param method - HTTP method string
 * @returns True if method is POST, PUT, PATCH, or DELETE
 */
function isMutatingOperation(method: string): boolean {
  return ["POST", "PUT", "PATCH", "DELETE"].includes(method.toUpperCase());
}

/**
 * Check if URL represents a filter/search operation
 */
function isFilterOperation(url: string): boolean {
  const filterPatterns = ["/filter", "/search", "/query"];
  return filterPatterns.some((pattern) => url.includes(pattern));
}

/**
 * Get entity key from request
 */
function getEntityKey(req: HttpRequest<unknown>): EntityKey | null {
  // 1. Check for explicit entity override in context
  const contextKey = req.context.get(ENTITY_KEY);
  if (contextKey) {
    return contextKey;
  }

  // 2. Extract from URL
  return extractEntityFromUrl(req.url);
}

/**
 * Extract entity key from URL using pattern matching
 */
function extractEntityFromUrl(url: string): EntityKey | null {
  const patterns: Array<{ regex: RegExp; key: EntityKey }> = [
    {
      regex: /\/price-list-categories(?:\/|$|\?)/,
      key: "price-list-categories",
    },
    // complexities MUST come before price-lists (URL: /price-lists/.../complexities)
    { regex: /\/complexities(?:\/|$|\?)/, key: "complexities" },
    { regex: /\/price-lists(?:\/|$|\?)/, key: "price-lists" },
    { regex: /\/project-subtasks(?:\/|$|\?)/, key: "project-subtasks" },
    { regex: /\/project-tasks(?:\/|$|\?)/, key: "project-tasks" },
    { regex: /\/project-users(?:\/|$|\?)/, key: "project-users" },
    // standard-subtasks MUST come before standard-tasks (URL: /standard-tasks/.../subtasks)
    { regex: /\/subtasks(?:\/|$|\?)/, key: "standard-subtasks" },
    { regex: /\/standard-tasks(?:\/|$|\?)/, key: "standard-tasks" },
    { regex: /\/contacts(?:\/|$|\?)/, key: "contacts" },
    { regex: /\/contracts(?:\/|$|\?)/, key: "contracts" },
    { regex: /\/quotes(?:\/|$|\?)/, key: "quotes" },
    { regex: /\/projects(?:\/|$|\?)/, key: "projects" },
    { regex: /\/timesheets(?:\/|$|\?)/, key: "timesheets" },
    { regex: /\/users(?:\/|$|\?)/, key: "users" },
    { regex: /\/tenants(?:\/|$|\?)/, key: "tenants" },
  ];

  for (const { regex, key } of patterns) {
    if (regex.test(url)) {
      return key;
    }
  }

  return null;
}

/**
 * TOAST INTERCEPTOR
 *
 * Automatically displays toast notifications for HTTP requests based on:
 * - Success responses (POST, PUT, PATCH, DELETE)
 * - Error responses (all methods)
 *
 * The interceptor:
 * 1. Detects the entity from the URL
 * 2. Retrieves appropriate messages from the entity registry
 * 3. Displays toast notifications
 * 4. Respects context tokens for custom behavior
 *
 * Configuration:
 * Add to your app.config.ts or module providers:
 * ```typescript
 * providers: [
 *   { provide: HTTP_INTERCEPTORS, useClass: ToastInterceptor, multi: true }
 * ]
 * ```
 */
@Injectable()
export class ToastInterceptor implements HttpInterceptor {
  private readonly toastService = inject(ToastService);
  private readonly messageBuilder = inject(MessageBuilderService);

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      tap({
        next: (event: HttpEvent<unknown>) => {
          if (event instanceof HttpResponse) {
            this.handleSuccess(req);
          }
        },
        error: (error: HttpErrorResponse) => {
          this.handleError(req, error);
        },
      }),
    );
  }

  /**
   * Handle successful HTTP responses.
   *
   * Shows success toast for mutating operations (POST, PUT, PATCH, DELETE)
   * unless suppressed via context token.
   */
  private handleSuccess(req: HttpRequest<unknown>): void {
    // Check if toast is suppressed
    if (req.context.get(SUPPRESS_SUCCESS_TOAST)) {
      return;
    }

    // Only show success toast for mutating operations
    if (!this.isMutatingOperation(req.method)) {
      return;
    }

    // Skip if it's a filter/search operation (POST but semantically GET)
    if (this.isFilterOperation(req.url)) {
      return;
    }

    // Check for custom message and description
    const customMessage = req.context.get(CUSTOM_SUCCESS_MESSAGE);
    const customDescription = req.context.get(CUSTOM_SUCCESS_DESCRIPTION) ?? undefined;

    if (customMessage) {
      this.toastService.success(customMessage, customDescription);
      return;
    }

    // Auto-generate message from entity registry
    const entityKey = this.getEntityKey(req);
    if (entityKey) {
      const message = this.messageBuilder.buildSuccessMessage(
        req.method as HttpMethod,
        entityKey,
      );
      this.toastService.success(message, customDescription);
    } else {
      // Fallback to generic message if entity not found
      this.toastService.success("Operazione completata con successo", customDescription);
    }
  }

  /**
   * Handle HTTP error responses.
   *
   * Always logs errors to console and shows error toast
   * unless suppressed via context token.
   */
  private handleError(
    req: HttpRequest<unknown>,
    error: HttpErrorResponse,
  ): void {
    // Check if toast is suppressed
    if (req.context.get(SUPPRESS_ERROR_TOAST)) {
      return;
    }

    // Check for custom error message from context
    const customMessage = req.context.get(CUSTOM_ERROR_MESSAGE);
    if (customMessage) {
      this.toastService.error(customMessage);
      return;
    }

    // Check for error message from backend response (priority over generic messages)
    const backendMessage = this.extractBackendErrorMessage(error);
    if (backendMessage) {
      // Try to translate the backend message using entity translations
      const entityKey = this.getEntityKey(req);
      const translated = translateBackendMessage(backendMessage, entityKey);
      this.toastService.error(translated);
      return;
    }

    // Fall back to auto-generated error message from entity registry
    const entityKey = this.getEntityKey(req);
    const message = this.messageBuilder.buildErrorMessage(
      error.status,
      entityKey || undefined,
    );
    this.toastService.error(message);
  }

  /**
   * Extract error message from backend response.
   *
   * Supports both string messages and arrays (class-validator errors).
   *
   * @param error - HTTP error response
   * @returns Error message string or null if not found
   */
  private extractBackendErrorMessage(error: HttpErrorResponse): string | null {
    // Check if error.error exists and has a message property
    if (error.error && typeof error.error === 'object' && 'message' in error.error) {
      const message = error.error.message;
      // Handle both string and array of strings (class-validator errors)
      if (typeof message === 'string') {
        return message;
      }
      if (Array.isArray(message) && message.length > 0) {
        return message[0];
      }
    }
    return null;
  }

  /**
   * Check if HTTP method is a mutating operation.
   *
   * @param method - HTTP method
   * @returns True if method is POST, PUT, PATCH, or DELETE
   */
  private isMutatingOperation(method: string): boolean {
    return ["POST", "PUT", "PATCH", "DELETE"].includes(method.toUpperCase());
  }

  /**
   * Check if URL represents a filter/search operation.
   *
   * Filter operations use POST for request body but are semantically GET.
   *
   * @param url - Request URL
   * @returns True if URL matches filter patterns
   */
  private isFilterOperation(url: string): boolean {
    const filterPatterns = ["/filter", "/search", "/query"];
    return filterPatterns.some((pattern) => url.includes(pattern));
  }

  /**
   * Get entity key from request.
   *
   * Priority:
   * 1. Context token (explicit override)
   * 2. URL pattern matching
   *
   * @param req - HTTP request
   * @returns Entity key or null if not found
   */
  private getEntityKey(req: HttpRequest<unknown>): EntityKey | null {
    // 1. Check for explicit entity override in context
    const contextKey = req.context.get(ENTITY_KEY);
    if (contextKey) {
      return contextKey;
    }

    // 2. Extract from URL
    return this.extractEntityFromUrl(req.url);
  }

  /**
   * Extract entity key from URL using pattern matching.
   *
   * Supports various URL formats:
   * - /api/o2-sales/price-lists/123 -> price-lists
   * - /api/o2-core/projects/abc -> projects
   * - /api/o2-users/users -> users
   *
   * @param url - Request URL
   * @returns Entity key or null if not matched
   */
  private extractEntityFromUrl(url: string): EntityKey | null {
    // Define patterns for each entity
    // Order matters: more specific patterns first
    const patterns: Array<{ regex: RegExp; key: EntityKey }> = [
      {
        regex: /\/price-list-categories(?:\/|$|\?)/,
        key: "price-list-categories",
      },
      // complexities MUST come before price-lists (URL: /price-lists/.../complexities)
      { regex: /\/complexities(?:\/|$|\?)/, key: "complexities" },
      { regex: /\/price-lists(?:\/|$|\?)/, key: "price-lists" },
      { regex: /\/project-subtasks(?:\/|$|\?)/, key: "project-subtasks" },
      { regex: /\/project-tasks(?:\/|$|\?)/, key: "project-tasks" },
      { regex: /\/project-users(?:\/|$|\?)/, key: "project-users" },
      // standard-subtasks MUST come before standard-tasks (URL: /standard-tasks/.../subtasks)
      { regex: /\/subtasks(?:\/|$|\?)/, key: "standard-subtasks" },
      { regex: /\/standard-tasks(?:\/|$|\?)/, key: "standard-tasks" },
      { regex: /\/contacts(?:\/|$|\?)/, key: "contacts" },
      { regex: /\/contracts(?:\/|$|\?)/, key: "contracts" },
      { regex: /\/quotes(?:\/|$|\?)/, key: "quotes" },
      { regex: /\/projects(?:\/|$|\?)/, key: "projects" },
      { regex: /\/timesheets(?:\/|$|\?)/, key: "timesheets" },
      { regex: /\/users(?:\/|$|\?)/, key: "users" },
      { regex: /\/tenants(?:\/|$|\?)/, key: "tenants" },
    ];

    for (const { regex, key } of patterns) {
      if (regex.test(url)) {
        return key;
      }
    }

    return null;
  }
}
